/**
 * Loading state for a list that belongs to one record, such as a vehicle's
 * warranties or an equipment's rent history. The vehicle and equipment
 * detail pages both use it, so their tabs load and go stale the same way.
 */
export type RecordListStatus = {
  loading: boolean;
  /** Record the list's rows belong to, or are loading for. */
  recordId: string | null;
  /** Request (record, page and search) the rows answer. */
  request: string | null;
  /** Only the latest request may fill the list. */
  requestId: string | null;
};

export const idleRecordList = (): RecordListStatus => ({
  loading: false,
  recordId: null,
  request: null,
  requestId: null,
});

/**
 * Marks a new request as pending. Returns true when it asks for different
 * rows (another record, page or search), so the caller clears the old ones;
 * a refresh of the same request keeps them on screen until new ones arrive.
 */
export const startRecordList = (
  status: RecordListStatus,
  { requestId, arg }: { requestId: string; arg: string | { id: string } },
) => {
  const request = JSON.stringify(arg);
  const changed = status.request !== request;
  status.loading = true;
  status.recordId = typeof arg === "string" ? arg : arg.id;
  status.request = request;
  status.requestId = requestId;
  return changed;
};

/** Ends the request. Returns false for a response a newer one superseded. */
export const finishRecordList = (
  status: RecordListStatus,
  requestId: string,
) => {
  if (status.requestId !== requestId) return false;
  status.loading = false;
  return true;
};

/**
 * Whether the rows belong to `recordId`, the record in the URL. Until they
 * do, another record's rows never render and the list counts as loading.
 */
export const recordListView = (
  status: RecordListStatus,
  recordId: string | undefined,
) => {
  const isCurrent = Boolean(recordId) && status.recordId === recordId;
  return { isCurrent, loading: status.loading || !isCurrent };
};
